import { useState, useLayoutEffect } from "react";

const findTransformedAncestor = (element) => {
  let current = element?.parentElement;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const hasTransformContext =
      style.transform !== "none" ||
      style.perspective !== "none" ||
      style.filter !== "none";

    if (hasTransformContext) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

export const useDropdownPosition = (state, inputRef) => {
  const [coords, setCoords] = useState({});

  useLayoutEffect(() => {
    if (!state || !inputRef) {
      return;
    }

    const inputElement = inputRef.current || inputRef;
    if (
      !inputElement ||
      typeof inputElement.getBoundingClientRect !== "function"
    ) {
      return;
    }

    const transformedAncestor = findTransformedAncestor(inputElement);

    const updatePosition = () => {
      const rect = inputElement.getBoundingClientRect();
      const dropdownHeight = 350; // default target dropdown height limit
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;

      let spaceBelow = viewportHeight - rect.bottom;
      let spaceAbove = rect.top;
      let position = "fixed";
      let leftValue = `${rect.left}px`;
      let rightValue = "auto";
      let maxWidth = `${Math.min(viewportWidth - rect.left - 10, 500)}px`;
      let topValue = `${rect.bottom + 2}px`;
      let bottomValue = "auto";

      // Inside transformed containers, fixed coordinates drift. Anchor absolutely inside that container.
      if (transformedAncestor) {
        const containerRect = transformedAncestor.getBoundingClientRect();
        const containerWidth =
          transformedAncestor.clientWidth || containerRect.width;
        const containerHeight =
          transformedAncestor.clientHeight || containerRect.height;

        position = "absolute";
        spaceBelow = containerRect.bottom - rect.bottom;
        spaceAbove = rect.top - containerRect.top;

        const localLeft = Math.max(0, rect.left - containerRect.left);
        const localRightSpace = Math.max(
          0,
          containerWidth - (rect.right - containerRect.left),
        );
        const isRightHalf = localLeft + rect.width / 2 > containerWidth / 2;

        if (isRightHalf) {
          leftValue = "auto";
          rightValue = `${localRightSpace}px`;
          maxWidth = `${Math.min(rect.right - containerRect.left - 10, 500)}px`;
        } else {
          leftValue = `${localLeft}px`;
          rightValue = "auto";
          maxWidth = `${Math.min(containerWidth - localLeft - 10, 500)}px`;
        }

        topValue = `${rect.bottom - containerRect.top + 2}px`;
        bottomValue = "auto";

        if (spaceBelow < 250 && spaceAbove > spaceBelow) {
          topValue = "auto";
          bottomValue = `${containerHeight - (rect.top - containerRect.top) + 2}px`;
        }
      } else {
        const isRightHalf = rect.left + rect.width / 2 > viewportWidth / 2;
        if (isRightHalf) {
          leftValue = "auto";
          rightValue = `${viewportWidth - rect.right}px`;
          maxWidth = `${Math.min(rect.right - 10, 500)}px`;
        }

        if (spaceBelow < 250 && spaceAbove > spaceBelow) {
          topValue = "auto";
          bottomValue = `${viewportHeight - rect.top + 2}px`;
        }
      }

      const style = {
        position,
        zIndex: 9999,
        width: "max-content",
        minWidth: `${rect.width}px`,
        left: leftValue,
        right: rightValue,
        top: topValue,
        bottom: bottomValue,
        maxWidth,
        maxHeight: `${Math.min(Math.max(120, (spaceBelow < 250 && spaceAbove > spaceBelow ? spaceAbove : spaceBelow) - 15), dropdownHeight)}px`,
      };

      setCoords(style);
    };

    updatePosition();

    window.addEventListener("resize", updatePosition);
    // Using capture to catch scrolls anywhere in the layout
    window.addEventListener("scroll", updatePosition, true);
    transformedAncestor?.addEventListener("scroll", updatePosition, true);

    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      transformedAncestor?.removeEventListener("scroll", updatePosition, true);
    };
  }, [state, inputRef]);

  return coords;
};
